import { type ActionEvent, Controller } from "@hotwired/stimulus"

import CarouselController from "@/controllers/carousel_controller"
import LoaderController from "@/controllers/loader_controller"
import SearchDateController from "@/controllers/search/search_date_controller"
import SearchBudgetController from "@/controllers/search/search_budget_controller"
import SearchExperienceController from "@/controllers/search/search_experience_controller"
import SearchGuestsController from "@/controllers/search/search_guests_controller"

import { saveNavigationTypeToStorage, selectContentEvent } from "@/helpers/ga4_helper"

const GIFTING_JOURNEY = document.body.getAttribute("data-gifting-journey") === "true"

export default class SearchFormController extends Controller {
  static outlets = [
    "carousel",
    "page-loader",
    "search-budget",
    "search-date",
    "search-experience",
    "search-guests"
  ]
  declare readonly carouselOutlet: CarouselController
  declare readonly pageLoaderOutlet: LoaderController
  declare readonly searchBudgetOutlet: SearchBudgetController
  declare readonly searchDateOutlet: SearchDateController
  declare readonly searchExperienceOutlet: SearchExperienceController
  declare readonly searchGuestsOutlet: SearchGuestsController

  static targets = [
    "budgetInput",
    "dateInput",
    "dateRadio",
    "experienceInput",
    "guestsInput",
    "journeyRadio"
  ]
  declare readonly budgetInputTarget: HTMLInputElement
  declare readonly dateInputTarget: HTMLInputElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly experienceInputTarget: HTMLInputElement
  declare readonly guestsInputTarget: HTMLInputElement
  declare readonly journeyRadioTargets: HTMLInputElement[]

  connect(): void {
    if (GIFTING_JOURNEY) {
      // Inputs disabled by default until controller loaded.
      this.journeyRadioTargets.forEach((input) => (input.disabled = false))
    }
  }

  experienceNextStep(): void {
    if (this.isGiftingJourney) {
      this.budgetInputTarget.focus()
    } else {
      this.guestsInputTarget.focus()
    }
  }

  jumpToSelectedDate(): void {
    this.carouselOutlet.jumpToIndex(this.selectedDateCarouselSlideIndex)
  }

  locationNextStep(): void {
    if (this.isGiftingJourney) {
      this.experienceInputTarget.focus()
    } else {
      this.dateInputTarget.focus()
    }
  }

  onDateFocus(): void {
    this.searchDateOutlet.selectCalendarTab()
    this.jumpToSelectedDate()
  }

  submit(): void {
    saveNavigationTypeToStorage("search_box")
    this.pageLoaderOutlet.startWithId(this.identifier)
  }

  setGuestsSummary(): void {
    this.guestsInputTarget.value = this.searchGuestsOutlet.summaryText
  }

  setBudgetSummary(): void {
    this.budgetInputTarget.value = this.searchBudgetOutlet.summaryText
  }

  setExperienceSummary({ params: { name } }: ActionEvent): void {
    this.experienceInputTarget.value = name
  }

  toggleJourney(): void {
    this.searchExperienceOutlet.resetExperienceSelection()

    if (this.isGiftingJourney) {
      this.searchGuestsOutlet.resetGuests()
      this.searchDateOutlet.deselectAllDates()
      selectContentEvent("Search Bar", "Buy a gift voucher")
    } else {
      this.searchBudgetOutlet.resetBudget()
      selectContentEvent("Search Bar", "Book a spa")
    }
  }

  private get selectedDateCarouselSlideIndex(): number {
    return this.selectedDateRadio
      ? this.carouselOutlet.slideListItemTargets.findIndex((slide) =>
          slide.contains(this.selectedDateRadio as Node)
        )
      : 0
  }

  private get selectedDateRadio(): HTMLInputElement | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)
  }

  private get isGiftingJourney(): boolean {
    return this.journeyRadioTargets.some((input) => input.checked && input.value === "gift")
  }
}
